import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, FlaskConical, Loader2 } from 'lucide-react';
import type { PresetFields, ProviderBody } from '../../api/imagePresets';
import { presetError } from '../../api/imagePresets';
import { useTrialGeneration } from '../../hooks';
import { getErrorDetail } from '../../utils/error';
import { AspectShape, Modal } from './common';

/** 응답 원문에서 이미지 base64. OpenAI 는 data[0].b64_json, Gemini 는 inline_data.data. */
const imageBase64 = (result: ProviderBody): string | null => {
  const openai = result.data?.[0]?.b64_json;
  if (openai) return openai;
  for (const candidate of result.candidates ?? []) {
    for (const part of candidate?.content?.parts ?? []) {
      const inline = part?.inline_data ?? part?.inlineData;
      if (inline?.data) return inline.data;
    }
  }
  return null;
};

const TrialGeneration: React.FC<{ draft: PresetFields; onClose: () => void }> = ({ draft, onClose }) => {
  const { t } = useTranslation();
  const [photos, setPhotos] = useState<File[]>([]);
  const [inputs, setInputs] = useState<Record<string, string>>({});
  const trial = useTrialGeneration();
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!trial.isPending || startedAt === null) return;
    const id = window.setInterval(() => setElapsed(Math.round((Date.now() - startedAt) / 1000)), 500);
    return () => window.clearInterval(id);
  }, [trial.isPending, startedAt]);

  const previews = useMemo(() => photos.map((f) => URL.createObjectURL(f)), [photos]);
  useEffect(() => () => previews.forEach((u) => URL.revokeObjectURL(u)), [previews]);

  const b64 = trial.data ? imageBase64(trial.data.result) : null;
  const resultUrl = b64 ? `data:image/png;base64,${b64}` : null;
  const error = presetError(trial.error);

  const run = () => {
    setStartedAt(Date.now());
    setElapsed(0);
    trial.mutate({ preset: draft, images: photos, inputs });
  };

  return (
    <Modal title={t('imagePresets.trialTitle', { name: draft.name || draft.id })} onClose={onClose} wide>
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-4">
          <p className="text-sm text-slate-600">{t('imagePresets.trialIntro')}</p>
          <div className="flex items-center gap-3 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
            <AspectShape aspect={draft.aspect} size={22} />
            <span>
              {draft.aspect} · {draft.provider} · <span className="font-mono">{draft.model}</span> ·{' '}
              {draft.quality ?? '—'}
              {draft.reference_images.length > 0 &&
                ` · ${t('imagePresets.withReferences', { count: draft.reference_images.length })}`}
            </span>
          </div>

          <div>
            <label htmlFor="trial-photos" className="block text-sm font-medium text-slate-700 mb-1">
              {t('imagePresets.trialPhotos')}
            </label>
            <input
              id="trial-photos"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple
              onChange={(e) => setPhotos(Array.from(e.target.files ?? []))}
              className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-lg file:border-0 file:bg-slate-100 file:px-3 file:py-2 file:text-sm file:font-medium hover:file:bg-slate-200"
            />
            {previews.length > 0 && (
              <div className="mt-2 flex gap-2">
                {previews.map((url, i) => (
                  <figure key={url} className="text-center">
                    <img src={url} alt="" className="h-20 w-20 rounded-lg object-cover border border-slate-200" />
                    <figcaption className="text-[11px] text-slate-500 mt-0.5">IMAGE {i + 1}</figcaption>
                  </figure>
                ))}
              </div>
            )}
          </div>

          {draft.variables.map((name) => (
            <div key={name}>
              <label htmlFor={`trial-var-${name}`} className="block text-sm font-medium text-slate-700 mb-1">
                {name}
              </label>
              <input
                id={`trial-var-${name}`}
                className="input"
                value={inputs[name] ?? ''}
                onChange={(e) => setInputs((prev) => ({ ...prev, [name]: e.target.value }))}
              />
            </div>
          ))}

          <p className="text-xs text-amber-700">{t('imagePresets.trialCost')}</p>
          <button
            type="button"
            onClick={run}
            disabled={photos.length === 0 || trial.isPending}
            className="btn-primary inline-flex items-center gap-2"
          >
            {trial.isPending ? <Loader2 size={16} className="animate-spin" /> : <FlaskConical size={16} />}
            {trial.isPending ? t('imagePresets.trialRunning', { seconds: elapsed }) : t('imagePresets.trialRun')}
          </button>

          {trial.isError && (
            <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700 space-y-1" role="alert">
              <p>
                <span className="font-mono text-xs">{error?.error_code}</span>{' '}
                {getErrorDetail(trial.error, t('imagePresets.trialFailed'))}
              </p>
              {error?.errors?.map((e) => (
                <p key={e.loc.join('.') + e.msg} className="text-xs">
                  {e.loc.join('.')}: {e.msg}
                </p>
              ))}
            </div>
          )}
        </div>

        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-slate-50 min-h-[20rem] p-3">
          {resultUrl ? (
            <figure className="w-full space-y-2">
              <img
                src={resultUrl}
                alt={t('imagePresets.trialResult')}
                className="mx-auto max-h-[60vh] rounded-lg shadow"
              />
              <figcaption className="flex flex-wrap items-center justify-between gap-2 text-xs text-slate-600">
                <span>
                  {trial.data?.result.size ?? ''} · {elapsed}s
                  {trial.data?.result.usage?.total_tokens && ` · ${trial.data.result.usage.total_tokens} tokens`}
                </span>
                <a
                  href={resultUrl}
                  download={`${draft.id}-trial.png`}
                  className="inline-flex items-center gap-1 text-blue-700 hover:underline"
                >
                  <Download size={13} /> {t('imagePresets.download')}
                </a>
              </figcaption>
            </figure>
          ) : trial.data ? (
            <p className="text-sm text-slate-500">{t('imagePresets.trialNoImage')}</p>
          ) : (
            <p className="text-sm text-slate-400">{t('imagePresets.trialPlaceholder')}</p>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default TrialGeneration;
